import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, IconButton, Slider, CircularProgress, Chip } from '@mui/material';
import { PiPlay, PiPause, PiDownloadSimple } from 'react-icons/pi';
import { apiService } from '../services/apiService';

interface PodcastPlayerProps {
  audioUrl?: string;
  s3Key?: string;
  jobName?: string;
  loading?: boolean;
  onRefreshUrl?: () => Promise<string>;
}

const STORAGE_PREFIX = 'podcast-position:';
const SAVE_INTERVAL_MS = 5000;
const PLAYBACK_SPEEDS = [1, 1.25, 1.5, 2];

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function PodcastPlayer({ audioUrl, s3Key, jobName, loading, onRefreshUrl }: PodcastPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speedIndex, setSpeedIndex] = useState(0);
  const [currentUrl, setCurrentUrl] = useState(audioUrl);
  const [downloading, setDownloading] = useState(false);

  // restore saved position on mount
  useEffect(() => {
    if (!s3Key || !audioRef.current) return;
    try {
      const saved = localStorage.getItem(`${STORAGE_PREFIX}${s3Key}`);
      if (saved) {
        const { position } = JSON.parse(saved);
        if (position > 0) audioRef.current.currentTime = position;
      }
    } catch { /* ignore corrupt localStorage */ }
  }, [s3Key, currentUrl]);

  // save position every 5 seconds while playing
  useEffect(() => {
    if (!isPlaying || !s3Key) return;
    const interval = setInterval(() => {
      if (audioRef.current) {
        localStorage.setItem(`${STORAGE_PREFIX}${s3Key}`, JSON.stringify({
          position: audioRef.current.currentTime,
          duration: audioRef.current.duration || 0,
          updatedAt: new Date().toISOString()
        }));
      }
    }, SAVE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isPlaying, s3Key]);

  // clear position when podcast ends
  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    if (s3Key) localStorage.removeItem(`${STORAGE_PREFIX}${s3Key}`);
  }, [s3Key]);

  // handle 403 (expired URL) by requesting a new one
  const handleError = useCallback(async () => {
    if (onRefreshUrl) {
      try {
        const newUrl = await onRefreshUrl();
        setCurrentUrl(newUrl);
      } catch { /* stay in error state */ }
    }
  }, [onRefreshUrl]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) { audioRef.current.pause(); }
    else { audioRef.current.play(); }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (_: Event, value: number | number[]) => {
    if (audioRef.current) audioRef.current.currentTime = value as number;
  };

  const cycleSpeed = () => {
    const next = (speedIndex + 1) % PLAYBACK_SPEEDS.length;
    setSpeedIndex(next);
    if (audioRef.current) audioRef.current.playbackRate = PLAYBACK_SPEEDS[next];
  };

  // Download via a blank window opened synchronously (avoids popup blocker).
  // The presigned URL includes response-content-disposition=attachment so S3
  // serves it as a download — no cross-origin fetch needed.
  const handleDownload = async () => {
    if (!s3Key && !currentUrl) return;
    setDownloading(true);

    // open blank window NOW while we're still in the synchronous click handler
    // so popup blockers allow it. we'll redirect it after the async url fetch.
    const downloadWindow = window.open('', '_blank', 'noopener');

    try {
      let url = currentUrl;
      if (s3Key) {
        const { presigned_url } = await apiService.getPodcastAudioUrl(s3Key);
        url = presigned_url;
      }
      if (downloadWindow && url) {
        downloadWindow.location.href = url;
      } else if (url) {
        // popup was blocked — try window.open as last resort
        window.open(url, '_blank', 'noopener');
      }
    } catch {
      if (downloadWindow) downloadWindow.close();
      if (currentUrl) window.open(currentUrl, '_blank', 'noopener');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary">
          Generating podcast{jobName ? ` for ${jobName}` : ''}...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <audio
        ref={audioRef}
        src={currentUrl}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
        onEnded={handleEnded}
        onError={handleError}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton onClick={togglePlay} size="small">
          {isPlaying ? <PiPause /> : <PiPlay />}
        </IconButton>
        <Typography variant="caption" sx={{ minWidth: 45 }}>
          {formatTime(currentTime)}
        </Typography>
        <Slider
          value={currentTime}
          max={duration || 100}
          onChange={handleSeek as any}
          size="small"
          sx={{ flex: 1 }}
        />
        <Typography variant="caption" sx={{ minWidth: 45 }}>
          {formatTime(duration)}
        </Typography>
        <Chip
          label={`${PLAYBACK_SPEEDS[speedIndex]}x`}
          size="small"
          onClick={cycleSpeed}
          variant="outlined"
          sx={{ cursor: 'pointer' }}
        />
        <IconButton
          size="small"
          onClick={handleDownload}
          disabled={downloading || (!s3Key && !currentUrl)}
        >
          <PiDownloadSimple />
        </IconButton>
      </Box>
    </Box>
  );
}
